import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const HERO = "/img/diagnosi.jpg";

export function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} className="btn-ghost w-full py-3 flex items-center justify-center gap-3 font-semibold" data-testid="google-login-button">
      <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      Continua con Google
    </button>
  );
}

export function AuthLayout({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Diagnosi" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#121212]/40 to-[#121212]" />
        <div className="absolute bottom-0 left-0 p-12 max-w-xl">
          <div className="eyebrow mb-4">Supporto tecnico remoto · su appuntamento</div>
          <h1 className="font-display text-5xl xl:text-6xl font-extrabold uppercase leading-[0.95]">
            Diagnosi precisa.<br /><span className="text-[#E50914]">Zero attese.</span>
          </h1>
          <p className="text-slate-300 mt-5 leading-relaxed">Identificazione veicolo via AI dal libretto, slot garantiti senza sovrapposizioni e sessione live su WhatsApp con il tecnico.</p>
          <div className="stripe-red h-1.5 w-40 mt-8" />
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 grid-bg">
        <div className="w-full max-w-md fade-up">
          <div className="font-display text-2xl font-extrabold uppercase mb-10">AutoTech<span className="text-[#E50914]">//</span>Pro</div>
          <h2 className="font-display text-4xl font-bold uppercase">{title}</h2>
          <p className="text-slate-400 mt-2 mb-8 text-sm">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      login(data);
      navigate(data.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title="Accedi" subtitle="Entra nella tua area officina o nel pannello tecnico.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label">Email</label>
          <input className="field" type="email" required value={form.email} data-testid="login-email-input"
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <label className="label">Password</label>
          <input className="field" type="password" required value={form.password} data-testid="login-password-input"
            onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        {error && <div className="text-sm text-red-400" data-testid="login-error">{error}</div>}
        <button className="btn-red w-full py-3 flex items-center justify-center gap-2" disabled={busy} data-testid="login-submit-button">
          {busy && <Loader2 size={16} className="animate-spin" />} Accedi
        </button>
      </form>
      <div className="flex items-center gap-3 my-6 text-xs text-slate-500 mono"><div className="h-px bg-[#383838] flex-1" /> OPPURE <div className="h-px bg-[#383838] flex-1" /></div>
      <GoogleButton />
      <p className="text-sm text-slate-400 mt-8">
        Nuovo cliente? <Link to="/registrati" className="text-[#E50914] font-semibold hover:underline" data-testid="go-register-link">Crea un account</Link>
      </p>
    </AuthLayout>
  );
}
